/**
 * ActionCard — Dashboard Build Engine action card (2026-07-28, P4; status +
 * done-transition 2026-08-12).
 *
 * The shared presentational card behind each Business-OS "build" action
 * (data / workflows / sites). Extracted from BuildActionCard once a 2nd concrete
 * card existed. Pure presentation: it renders an icon + title + description, an
 * optional preview of the tenant's own answer, and a button that fires onAction
 * (each card wires that to startPiRoutine with its own seed).
 *
 * When `status` is provided the card also shows a per-step status chip (To do /
 * In progress / Done) and a user-attested control: "Mark done" while
 * in_progress, "Reopen" once done. This is the tenant's own step tracker — NOT a
 * system-claimed completion (design §10: the Progress board stays the real
 * evidence surface; this is a checklist the user drives).
 */
import type { ReactNode } from 'react';
import type { BuildStepStatus } from '@/stores/dashboardStore';

export interface ActionCardProps {
  /** Root data-testid (e.g. 'workflows-action-card'). */
  testId: string;
  icon: ReactNode;
  title: string;
  description: string;
  /** The tenant's own answer, shown quoted; omitted → no preview box. */
  preview?: string;
  previewTestId?: string;
  buttonLabel: string;
  buttonTestId: string;
  onAction: () => void;
  /** The step's tracked status. Omitted → no status chip/control (pure P4 card). */
  status?: BuildStepStatus;
  /** Mark the step done (shown while in_progress). */
  onMarkDone?: () => void;
  /** Reopen a done step (shown while done). */
  onReopen?: () => void;
}

const STATUS_LABEL: Record<BuildStepStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

const STATUS_CLASS: Record<BuildStepStatus, string> = {
  todo: 'text-vibe-text-secondary',
  in_progress: 'text-amber-400',
  done: 'text-emerald-400',
};

export function ActionCard({
  testId,
  icon,
  title,
  description,
  preview,
  previewTestId,
  buttonLabel,
  buttonTestId,
  onAction,
  status,
  onMarkDone,
  onReopen,
}: ActionCardProps) {
  return (
    <div
      data-testid={testId}
      className="w-full max-w-2xl rounded-lg border border-vibe-border bg-vibe-bg-secondary p-5"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-vibe-accent/15 text-vibe-accent">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-vibe-text">{title}</h3>
            {status !== undefined && (
              <span
                data-testid={`${testId}-status`}
                className={`shrink-0 rounded-full bg-vibe-bg px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}
              >
                {STATUS_LABEL[status]}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-vibe-text-secondary">{description}</p>
          {preview !== undefined && (
            <p
              data-testid={previewTestId}
              className="mt-2 rounded bg-vibe-bg px-3 py-2 text-sm italic text-vibe-text-secondary"
            >
              “{preview.trim()}”
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid={buttonTestId}
              onClick={onAction}
              className="rounded bg-vibe-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-vibe-accent-hover"
            >
              {buttonLabel}
            </button>
            {status === 'in_progress' && onMarkDone && (
              <button
                type="button"
                data-testid={`${testId}-mark-done`}
                onClick={onMarkDone}
                className="rounded px-3 py-2 text-sm font-medium text-emerald-400 transition-colors hover:bg-vibe-bg"
              >
                Mark done
              </button>
            )}
            {status === 'done' && onReopen && (
              <button
                type="button"
                data-testid={`${testId}-reopen`}
                onClick={onReopen}
                className="rounded px-3 py-2 text-sm font-medium text-vibe-text-secondary transition-colors hover:bg-vibe-bg hover:text-vibe-text"
              >
                Reopen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActionCard;
